/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Checkbox,
    Combobox,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Input,
    makeStyles,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Option,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens,
} from '@fluentui/react-components';
import { Delete24Regular, Dismiss24Regular, Edit24Regular, Save24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RequiredFieldFrame from '../../components/RequiredFieldFrame';
import { useProject } from '../../context/ProjectContext';
import type { ProjectUserInfo } from '../../models/ProjectUserInfo';
import type { ProjektItem } from '../../models/ProjektItem';
import type { ProjektmitarbeiterItem } from '../../models/ProjektmitarbeiterItem';
import { DataService } from '../../services/dataService';
import useConfigStore from '../../store/config';
import { getVisibleMissingEckdatenFields, isRequiredFieldMissing } from '../../utils/eckdatenCompleteness';

interface PermissionConfig {
    isBenutzerProjektLeiter?: 'r' | 'rw';
    isBenutzerProjektleiterAssistent?: 'r' | 'rw';
    isRegularUser?: 'r' | 'rw';
    'Rolle Administratoren'?: 'r' | 'rw';
    'Rolle Hochschulleitung'?: 'r' | 'rw';
    'Rolle Controlling'?: 'r' | 'rw';
    'Rolle Accounting'?: 'r' | 'rw';
    'Rolle Vertragskoordination'?: 'r' | 'rw';
    'Rolle Forschungsservice'?: 'r' | 'rw';
    'Rolle Rektorat'?: 'r' | 'rw';
    departmentHead?: 'r' | 'rw';
}

/**
 * Determine if the current user can edit a field based on their role and permissions
 */
const canEditField = (
    fieldPermissions: PermissionConfig | undefined,
    userRole: string | null,
    project: ProjektItem | null,
    userInfo: ProjectUserInfo | null
): boolean => {
    if (!fieldPermissions || !project) return false;

    // Check if user is a project manager
    if (project.isBenutzerProjektLeiter && fieldPermissions.isBenutzerProjektLeiter === 'rw') {
        return true;
    }

    // Check if user is a project manager assistant
    if (project.isBenutzerProjektleiterAssistent && fieldPermissions.isBenutzerProjektleiterAssistent === 'rw') {
        return true;
    }

    // Check if user is department head
    if (userInfo && project.departmentLeiterID === userInfo.userId && fieldPermissions.departmentHead === 'rw') {
        return true;
    }

    // Check role-based permissions
    if (userRole === 'Administratoren' && fieldPermissions['Rolle Administratoren'] === 'rw') {
        return true;
    }

    if (userRole === 'Hochschulleitung' && fieldPermissions['Rolle Hochschulleitung'] === 'rw') {
        return true;
    }

    if (userRole === 'Controlling' && fieldPermissions['Rolle Controlling'] === 'rw') {
        return true;
    }

    if (userRole === 'Accounting' && fieldPermissions['Rolle Accounting'] === 'rw') {
        return true;
    }

    if (userRole === 'Vertragskoordination' && fieldPermissions['Rolle Vertragskoordination'] === 'rw') {
        return true;
    }

    if (userRole === 'Forschungsservice' && fieldPermissions['Rolle Forschungsservice'] === 'rw') {
        return true;
    }

    if (userRole === 'Rektorat' && fieldPermissions['Rolle Rektorat'] === 'rw') {
        return true;
    }

    // Check if user is regular user/viewer
    if (!project.isBenutzerProjektLeiter &&
        !project.isBenutzerProjektleiterAssistent &&
        !project.isBenutzerProjektAssistent &&
        fieldPermissions.isRegularUser === 'rw') {
        return true;
    }

    return false;
};

/**
 * Determine if the current user can view a field (read permission)
 */
const canViewField = (
    fieldPermissions: PermissionConfig | undefined,
    userRole: string | null,
    project: ProjektItem | null,
    userInfo: ProjectUserInfo | null
): boolean => {
    if (!fieldPermissions || !project) return false;

    // Check if user is a project manager (read or write)
    if (project.isBenutzerProjektLeiter &&
        (fieldPermissions.isBenutzerProjektLeiter === 'rw' || fieldPermissions.isBenutzerProjektLeiter === 'r')) {
        return true;
    }

    // Check if user is a project manager assistant (read or write)
    if (project.isBenutzerProjektleiterAssistent &&
        (fieldPermissions.isBenutzerProjektleiterAssistent === 'rw' || fieldPermissions.isBenutzerProjektleiterAssistent === 'r')) {
        return true;
    }

    // Check if user is department head
    if (userInfo && project.departmentLeiterID === userInfo.userId &&
        (fieldPermissions.departmentHead === 'rw' || fieldPermissions.departmentHead === 'r')) {
        return true;
    }

    // Check role-based permissions (read or write)
    const rolePermission = userRole ?
        (fieldPermissions[`Rolle ${userRole}` as keyof PermissionConfig] ||
            fieldPermissions[userRole as keyof PermissionConfig]) :
        undefined;

    if (rolePermission && (rolePermission === 'rw' || rolePermission === 'r')) {
        return true;
    }

    // Check if user is regular user/viewer
    if (!project.isBenutzerProjektLeiter &&
        !project.isBenutzerProjektleiterAssistent &&
        !project.isBenutzerProjektAssistent &&
        (fieldPermissions.isRegularUser === 'rw' || fieldPermissions.isRegularUser === 'r')) {
        return true;
    }

    return false;
};

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalL,
    },
    headerTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorNeutralForeground1,
    },
    categorySection: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalL,
        paddingBottom: tokens.spacingVerticalL,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    categoryHeader: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorBrandForeground1,
        paddingTop: '2px',
    },
    fieldsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
    },
    fieldBox: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalM,
        alignItems: 'start',
        paddingBottom: 0,
    },
    fieldLabel: {
        fontWeight: 600,
        fontSize: '11px',
        color: tokens.colorNeutralForeground2,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        paddingTop: '4px',
    },
    fieldValue: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        wordWrap: 'break-word',
    },
    emptyValue: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
    },
    booleanValue: {
        padding: '2px 6px',
        borderRadius: '2px',
        fontSize: '11px',
        fontWeight: 600,
        display: 'inline-block',
        width: 'fit-content',
    },
    booleanTrue: {
        backgroundColor: tokens.colorStatusSuccessBackground2,
        color: tokens.colorStatusSuccessForeground1,
    },
    booleanFalse: {
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground3,
    },
    inputField: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
        height: '32px',
    },
    largeTextarea: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
        minHeight: '120px',
        resize: 'none',
    },
    tableContainer: {
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '4px',
        overflow: 'hidden',
        flex: 1,
        minWidth: '200px',
    },
    table: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
    },
    tableHeaderRow: {
        backgroundColor: tokens.colorNeutralBackground3,
    },
});

interface EckdatenESGFundingProps {
    project: ProjektItem | null;
}

interface FieldDefinition {
    label: string;
    key: keyof ProjektItem | string;
    isBoolean?: boolean;
    isArray?: boolean;
    editable?: boolean;
}

interface DataGroup {
    title: string;
    fields: FieldDefinition[];
}

interface EditedValues {

    [key: string]: string | boolean | number[] | any[] | null | undefined;
}

const initializeEditedValues = (project: ProjektItem | null): EditedValues => {
    if (!project) return {};
    return {
        projektauftraggeber: String(project.foerdergeberNeuID || ''),
        mittelherkunft: project.mittelherkunft || '',
        foerderschiene: String(project.foerderschieneID || ''),
        leadpartner: project.leadpartner ?? null,
        externeProjektnummer: project.externeProjektnummer || '',
        auftragsforschung: project.auftragsforschung || false,
        forschungsschwerpunktInternID: String(project.forschungsschwerpunktInternID || ''),
        forschungsschwerpunktIntern: project.forschungsschwerpunktIntern || '',
        forschungsschwerpunkt2012ID: String(project.forschungsschwerpunkt2012ID || ''),
        nachhaltigkeitszieleCombinedAsText: project.nachhaltigkeitszieleCombinedAsText || '',
        selectedNachhaltigkeitsziele: project.nachhaltigkeitsziele || [],
        studiengangZuordnung: project.studiengangZuordnung || [],
        zustaendigerMaForschung: project.zustaendigerMaForschungId ? String(project.zustaendigerMaForschungId) : '',
    };
};

const EckdatenESGFunding: React.FC<EckdatenESGFundingProps> = ({ project: _project }) => {
    const styles = useStyles();
    const { selectedRole } = useConfigStore((state) => state.roleConfig);
    const { userInfo: configUserInfo } = useConfigStore((state) => state.userConfig);
    const { accounts, instance } = useMsal();
    const { refreshProject, userInfo } = useProject();
    const [isEditing, setIsEditing] = useState(false);
    const [editedValues, setEditedValues] = useState<EditedValues>(() => initializeEditedValues(_project));
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [nachhaltigkeitszielOptions, setNachhaltigkeitszielOptions] = useState<Array<{ value: number; text: string }>>([]);
    const [forschungsschwerpunkteOptions, setForschungsschwerpunkteOptions] = useState<Array<{ value: number; text: string }>>([]);
    const [oefosOptions, setOefosOptions] = useState<Array<{ value: number; text: string }>>([]);
    const [oefosQuery, setOefosQuery] = useState<string | null>(null);
    const [studiengangOptions, setStudiengangOptions] = useState<Array<{ id: string; text: string }>>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState<'edit' | 'add' | null>(null);
    const [editingStudiengangIndex, setEditingStudiengangIndex] = useState<number | null>(null);
    const [editingPercentage, setEditingPercentage] = useState('');
    const [newStudiengangId, setNewStudiengangId] = useState<string | null>(null);
    const [studiengangDisplayValue, setStudiengangDisplayValue] = useState('');
    const [newPercentage, setNewPercentage] = useState('');
    const [foerdergeberNeuOptions, setFoerdergeberNeuOptions] = useState<Array<{ value: string; text: string }>>([]);
    const [foerderschieneOptions, setFoerderschieneOptions] = useState<Array<{ value: string; text: string }>>([]);
    const [mitarbeiterOptions, setMitarbeiterOptions] = useState<ProjektmitarbeiterItem[]>([]);
    const [forschungMemberSearchResults, setForschungMemberSearchResults] = useState<ProjektmitarbeiterItem[]>([]);
    const [forschungMemberSearchInputValue, setForschungMemberSearchInputValue] = useState('');
    const [optionsLoaded, setOptionsLoaded] = useState(false);
    const missingFields = useMemo(() => getVisibleMissingEckdatenFields(_project, userInfo), [_project, userInfo]);
    const memberSearchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // All field permissions configuration (based on CSV mapping)
    const allFieldPermissions = useMemo(() => {
        const permissions: Array<{ key: string; permissions: PermissionConfig }> = [
            { key: 'projektauftraggeber', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'mittelherkunft', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'foerderschiene', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'leadpartner', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'externeProjektnummer', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'studiengangZuordnung', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'auftragsforschung', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'forschungsschwerpunktInternID', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'forschungsschwerpunkt2012ID', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'nachhaltigkeitszieleCombinedAsText', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'zustaendigerMaForschung', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Forschungsservice': 'rw', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Rektorat': 'r' } },
        ];
        return permissions;
    }, []);

    // Determine if current user can edit any fields
    const canEditAny = useMemo(() => {
        if (!_project || !configUserInfo) return false;

        // Check if user can edit at least one field
        return allFieldPermissions.some((field) => {
            return canEditField(field.permissions, selectedRole, _project, configUserInfo);
        });
    }, [_project, selectedRole, configUserInfo, allFieldPermissions]);

    // DataService instance
    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    useEffect(() => {
        setEditedValues(initializeEditedValues(_project));
    }, [_project]);

    // Fetch mitarbeiter when entering edit mode
    const fetchMitarbeiterOptions = useCallback(async () => {
        if (!dataService || optionsLoaded) return;

        try {
            const mitarbeiter = await dataService.getMitarbeiter();
            setMitarbeiterOptions(mitarbeiter);
            setForschungMemberSearchResults(mitarbeiter);
            setOptionsLoaded(true);
        } catch (error) {
            console.error('Error fetching mitarbeiter:', error);
        }
    }, [dataService, optionsLoaded]);

    useEffect(() => {
        if (isEditing && !optionsLoaded && dataService) {
            fetchMitarbeiterOptions();
        }
    }, [isEditing, optionsLoaded, dataService, fetchMitarbeiterOptions]);

    // Handle member search for Research Staff Member
    const handleForschungMemberSearch = useCallback((searchTerm: string) => {
        if (memberSearchTimeoutRef.current) clearTimeout(memberSearchTimeoutRef.current);

        if (searchTerm.length < 2) {
            setForschungMemberSearchResults([]);
            return;
        }

        memberSearchTimeoutRef.current = setTimeout(() => {
            const filtered = mitarbeiterOptions.filter((member) => {
                const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                return fullName.toLowerCase().includes(searchTerm.toLowerCase());
            });
            setForschungMemberSearchResults(filtered);
        }, 300);
    }, [mitarbeiterOptions]);

    // Initialize member search input value for Forschung when entering edit mode
    useEffect(() => {
        if (isEditing && mitarbeiterOptions.length > 0) {
            const currentMemberId = parseInt(editedValues.zustaendigerMaForschung as string);
            if (currentMemberId) {
                const member = mitarbeiterOptions.find((m) => m.personalID === currentMemberId);
                if (member) {
                    const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                    setForschungMemberSearchInputValue(fullName);
                } else {
                    setForschungMemberSearchInputValue('');
                }
            } else {
                setForschungMemberSearchInputValue('');
            }
        } else if (!isEditing) {
            setForschungMemberSearchInputValue('');
        }
    }, [isEditing, mitarbeiterOptions, editedValues.zustaendigerMaForschung]);

    // Options filtered by the typed text (case-insensitive substring match)
    const filteredOefosOptions = useMemo(() => {
        const query = (oefosQuery ?? '').trim().toLowerCase();
        if (!query) return oefosOptions;
        return oefosOptions.filter((option) => option.text.toLowerCase().includes(query));
    }, [oefosOptions, oefosQuery]);

    // Fetch options for Nachhaltigkeitsziele, Studiengaenge, Foerdergeber, Foerderschiene and OEFOS
    useEffect(() => {
        if (!dataService) return;

        const fetchOptions = async () => {
            try {
                const [nachhaltigData, forschungsData, studienData, foerdergeberData, foerderschieneData, oefosData] = await Promise.all([
                    dataService.getNachhaltigkeitsziele(),
                    dataService.getForschungsschwerpunkte(),
                    dataService.getStudiengaenge(),
                    dataService.getFoerdergeberNeu(),
                    dataService.getFoerderschieneItems(),
                    dataService.getForschungsschwerpunkteOefos(),
                ]);

                if (oefosData) {
                    setOefosOptions(oefosData.map((item) => ({
                        value: item.code,
                        text: item.code + ' - ' + (item.bezeichnungEn || ''),
                    })));
                }

                if (nachhaltigData) {
                    const transformed = nachhaltigData.map((item: any) => ({
                        value: parseInt(item.value, 10),
                        text: item.text || item.kurzbezeichnung || '',
                    }));
                    setNachhaltigkeitszielOptions(transformed);
                }

                if (forschungsData) {
                    const transformed = forschungsData.map((item: any) => ({
                        value: parseInt(item.value, 10),
                        text: item.text || item.kurzbezeichnung || '',
                    }));
                    setForschungsschwerpunkteOptions(transformed);
                }

                if (studienData) {
                    // Deduplicate by key to avoid duplicate keys
                    const uniqueStudiengaenge = studienData.reduce((acc: any[], item: any) => {
                        if (!acc.find((s) => s.id === item.key)) {
                            acc.push({
                                id: item.key,  // Use item.key instead of item.id
                                text: item.text,
                            });
                        }
                        return acc;
                    }, []);
                    setStudiengangOptions(uniqueStudiengaenge);
                }

                if (foerdergeberData) {
                    const transformed = foerdergeberData.map((item: any) => ({
                        value: item.key || item.value,
                        text: item.text,
                    }));
                    setFoerdergeberNeuOptions(transformed);
                }

                if (foerderschieneData) {
                    const transformed = foerderschieneData.map((item: any) => ({
                        value: item.key || item.value,
                        text: item.text,
                    }));
                    setFoerderschieneOptions(transformed);
                }
            } catch (error) {
                console.error('Error fetching options:', error);
            }
        };

        fetchOptions();
    }, [dataService]);

    const handleFieldChange = (key: string, value: string | boolean | null) => {
        setEditedValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    // Handlers for Study Program editing and adding
    const handleEditStudiengang = (index: number) => {
        const studiengangData = (editedValues.studiengangZuordnung as any[])[index];
        if (studiengangData) {
            setEditingStudiengangIndex(index);
            setEditingPercentage(String(studiengangData.prozent || 0));
            setDrawerMode('edit');
            setDrawerOpen(true);
        }
    };

    const handleOpenAddDrawer = () => {
        setEditingStudiengangIndex(null);
        setNewStudiengangId(null);
        setStudiengangDisplayValue('');
        setNewPercentage('');
        setDrawerMode('add');
        setDrawerOpen(true);
    };

    const handleSaveStudiengangEdit = () => {
        if (drawerMode === 'edit' && editingStudiengangIndex !== null && editingPercentage) {
            const updated = [...(editedValues.studiengangZuordnung as any[])];
            const percentValue = parseInt(editingPercentage, 10);
            updated[editingStudiengangIndex] = {
                ...updated[editingStudiengangIndex],
                prozent: percentValue,
                prozentAsText: `${percentValue}%`,
            };
            setEditedValues((prev) => ({
                ...prev,
                studiengangZuordnung: updated,
            }));
            setDrawerOpen(false);
            setDrawerMode(null);
            setEditingStudiengangIndex(null);
            setEditingPercentage('');
        } else if (drawerMode === 'add' && newStudiengangId && newPercentage) {
            const selectedStudiengang = studiengangOptions.find((s) => s.id === newStudiengangId);
            if (selectedStudiengang) {
                const percentValue = parseInt(newPercentage, 10);
                const newEntry = {
                    studiengangID: newStudiengangId,
                    kurzbezeichnung: selectedStudiengang.text,
                    prozent: percentValue,
                    prozentAsText: `${percentValue}%`,
                };
                setEditedValues((prev) => ({
                    ...prev,
                    studiengangZuordnung: [...(prev.studiengangZuordnung as any[]), newEntry],
                }));
                setDrawerOpen(false);
                setDrawerMode(null);
                setNewStudiengangId(null);
                setNewPercentage('');
            }
        }
    };

    const handleRemoveStudiengang = (index: number) => {
        setEditedValues((prev) => ({
            ...prev,

            studiengangZuordnung: (prev.studiengangZuordnung as any[]).filter((_, i) => i !== index),
        }));
    };

    const handleSave = async () => {
        if (!_project || !dataService) {
            setErrorMessage('Unable to save: Missing project or service');
            return;
        }

        setIsSaving(true);
        try {
            setErrorMessage(null);

            const selectedForschungsschwerpunktId = parseInt(editedValues.forschungsschwerpunktInternID as string, 10);
            const selectedForschungsschwerpunkt = forschungsschwerpunkteOptions.find((opt) => opt.value === selectedForschungsschwerpunktId);
            const selectedOefosId = parseInt(editedValues.forschungsschwerpunkt2012ID as string, 10);
            const selectedOefos = oefosOptions.find((opt) => opt.value === selectedOefosId);

            const updateData: ProjektItem = {
                ..._project,
                foerdergeberNeuID: (editedValues.projektauftraggeber as string) ? parseInt(editedValues.projektauftraggeber as string, 10) : (_project.foerdergeberNeuID || 0),
                mittelherkunft: (editedValues.mittelherkunft as string) || _project.mittelherkunft || '',
                foerderschieneID: (editedValues.foerderschiene as string) ? parseInt(editedValues.foerderschiene as string, 10) : (_project.foerderschieneID || 0),
                leadpartner: editedValues.leadpartner as boolean | null,
                externeProjektnummer: (editedValues.externeProjektnummer as string) || _project.externeProjektnummer || '',
                auftragsforschung: (editedValues.auftragsforschung as boolean) !== undefined ? (editedValues.auftragsforschung as boolean) : _project.auftragsforschung,
                forschungsschwerpunktInternID: selectedForschungsschwerpunkt ? selectedForschungsschwerpunkt.value : (_project.forschungsschwerpunktInternID || 0),
                forschungsschwerpunktIntern: selectedForschungsschwerpunkt ? selectedForschungsschwerpunkt.text : (_project.forschungsschwerpunktIntern || ''),
                forschungsschwerpunkt2012ID: selectedOefosId ? (selectedOefos ? selectedOefos.value : (_project.forschungsschwerpunkt2012ID ?? 0)) : 0,
                forschungsschwerpunkt2012: selectedOefosId ? (selectedOefos ? selectedOefos.text : (_project.forschungsschwerpunkt2012 ?? null)) : null,

                nachhaltigkeitsziele: (editedValues.selectedNachhaltigkeitsziele as number[]) || _project.nachhaltigkeitsziele || [],
                studiengangZuordnung: (editedValues.studiengangZuordnung as ProjektItem['studiengangZuordnung']) || _project.studiengangZuordnung || [],
                zustaendigerMaForschungId: (editedValues.zustaendigerMaForschung as string) ? parseInt(editedValues.zustaendigerMaForschung as string, 10) : undefined,
            };

            const result = await dataService.updateProjekt(updateData);

            if (result.success) {
                if (refreshProject) {
                    await refreshProject();
                }
                setShowSuccessMessage(true);
                setErrorMessage(null);
                setTimeout(() => setShowSuccessMessage(false), 3000);
                setIsEditing(false);
            } else {
                setErrorMessage(result.message || 'Failed to save changes');
            }
        } catch (error) {
            console.error('Error saving ESG & Funding data:', error);
            setErrorMessage('An error occurred while saving');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (isSaving) return;
        setEditedValues(initializeEditedValues(_project));
        setIsEditing(false);
    };

    // Define fields for ESG & Funding tab based on CSV mapping
    const dataGroups: DataGroup[] = [
        {
            title: 'Funding Details',
            fields: [
                { label: 'Funding Source', key: 'projektauftraggeber', editable: true },
                { label: 'Source of Funds', key: 'mittelherkunft', editable: true },
                { label: 'Funding Track (Legacy)', key: 'foerderschiene', editable: true },
                { label: 'Research Staff Member', key: 'zustaendigerMaForschung', editable: true },
                { label: 'Lead Partner', key: 'leadpartner', editable: true },
                { label: 'External Project Number', key: 'externeProjektnummer', editable: true },
            ],
        },
        {
            title: 'Project Classification',
            fields: [
                { label: 'Study Program Assignment (BIS Report)', key: 'studiengangZuordnung', isArray: true, editable: false },
                { label: 'Contract Research', key: 'auftragsforschung', isBoolean: true, editable: true },
                { label: 'Research Focus per IMC Definition', key: 'forschungsschwerpunktInternID', editable: true },
                { label: 'Research Focus (ÖFOS 2012)', key: 'forschungsschwerpunkt2012ID', editable: true },
                { label: 'Sustainability Goals (SDGs)', key: 'nachhaltigkeitszieleCombinedAsText', editable: true },
            ],
        },
    ];

    const renderStudiengangTable = () => {
        if (!_project) return null;


        const displayStudiengang = isEditing && editedValues.studiengangZuordnung
            ? (editedValues.studiengangZuordnung as any[])
            : _project?.studiengangZuordnung;

        // Not an early return: the drawer must stay mounted so "Add" also works when the list is empty
        const hasStudiengaenge = !!displayStudiengang && displayStudiengang.length > 0;

        return (
            <>
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>Study Program Assignment (BIS Report)</div>
                    <div style={{ width: '50%' }}>
                        {!hasStudiengaenge && (
                            <div className={`${styles.fieldValue} ${styles.emptyValue}`}>No data available</div>
                        )}
                        {hasStudiengaenge && (
                            <div className={styles.tableContainer}>
                                <Table className={styles.table}>
                                    <TableHeader className={styles.tableHeaderRow}>
                                        <TableRow>
                                            {isEditing && <TableHeaderCell style={{ width: '80px', textAlign: 'center' }}>Actions</TableHeaderCell>}
                                            <TableHeaderCell>Study Program</TableHeaderCell>
                                            <TableHeaderCell>Percent</TableHeaderCell>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(displayStudiengang ?? []).map((item, index: number) => {

                                            const typedItem = item as any;
                                            return (
                                                <TableRow key={index} onClick={() => isEditing && handleEditStudiengang(index)} style={{ cursor: isEditing ? 'pointer' : 'default' }}>
                                                    {isEditing && (
                                                        <TableCell style={{ textAlign: 'center' }}>
                                                            <Button
                                                                icon={<Delete24Regular />}
                                                                appearance="subtle"
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveStudiengang(index);
                                                                }}
                                                                title="Remove"
                                                                style={{ color: tokens.colorStatusDangerForeground1 }}
                                                            />
                                                        </TableCell>
                                                    )}
                                                    <TableCell>{typedItem.kurzbezeichnung || '-'}</TableCell>
                                                    <TableCell>{typedItem.prozentAsText || `${typedItem.prozent || 0}%`}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                        {/* Add Button */}
                        {isEditing && (
                            <div style={{ marginTop: tokens.spacingVerticalM }}>
                                <Button appearance="primary" onClick={handleOpenAddDrawer}>
                                    Add Study Program
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Study Program Drawer - Edit or Add Mode */}
                <Drawer
                    open={drawerOpen}
                    onOpenChange={(_, { open }) => {
                        if (!open) {
                            setDrawerOpen(false);
                            setDrawerMode(null);
                            setEditingStudiengangIndex(null);
                            setEditingPercentage('');
                            setNewStudiengangId(null);
                            setStudiengangDisplayValue('');
                            setNewPercentage('');
                        }
                    }}
                    position="end"
                >
                    <DrawerHeader>
                        <DrawerHeaderTitle>
                            {drawerMode === 'edit' ? 'Edit Study Program Percentage' : 'Add Study Program Assignment'}
                        </DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody>
                        {drawerMode === 'edit' && editingStudiengangIndex !== null && displayStudiengang?.[editingStudiengangIndex] && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '4px' }}>
                                        Study Program
                                    </label>
                                    <div style={{ padding: tokens.spacingVerticalS, backgroundColor: tokens.colorNeutralBackground3, borderRadius: '4px' }}>
                                        {displayStudiengang?.[editingStudiengangIndex]?.kurzbezeichnung}
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '4px' }}>
                                        Percentage *
                                    </label>
                                    <Input
                                        type="number"
                                        value={editingPercentage}
                                        onChange={(_ev, data) => {
                                            setEditingPercentage(data.value);
                                        }}
                                        min="0"
                                        max="100"
                                        className={styles.inputField}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, marginTop: tokens.spacingVerticalL }}>
                                    <Button appearance="primary" onClick={handleSaveStudiengangEdit}>
                                        Save
                                    </Button>
                                    <Button appearance="secondary" onClick={() => setDrawerOpen(false)}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                        {drawerMode === 'add' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '4px' }}>
                                        Study Program *
                                    </label>
                                    <Combobox
                                        value={studiengangDisplayValue}
                                        selectedOptions={newStudiengangId ? [newStudiengangId] : []}
                                        onOptionSelect={(_ev, data) => {
                                            if (data.selectedOptions && data.selectedOptions.length > 0) {
                                                const selectedId = data.selectedOptions[0];
                                                setNewStudiengangId(selectedId);
                                                const selectedOption = studiengangOptions.find((o) => o.id === selectedId);
                                                if (selectedOption) {
                                                    setStudiengangDisplayValue(selectedOption.text);
                                                }
                                            }
                                        }}
                                        placeholder="Select an study program"
                                        style={{ width: '100%' }}
                                    >
                                        {studiengangOptions.map((option, index) => (
                                            <Option
                                                key={`${option.id}-${index}`}
                                                value={String(option.id)}
                                                text={option.text}
                                            >
                                                {option.text}
                                            </Option>
                                        ))}
                                    </Combobox>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '4px' }}>
                                        Percentage *
                                    </label>
                                    <Input
                                        type="number"
                                        value={newPercentage}
                                        onChange={(_ev, data) => {
                                            setNewPercentage(data.value);
                                        }}
                                        placeholder="0"
                                        min="0"
                                        max="100"
                                        className={styles.inputField}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, marginTop: tokens.spacingVerticalL }}>
                                    <Button
                                        appearance="primary"
                                        onClick={handleSaveStudiengangEdit}
                                        disabled={!newStudiengangId || !newPercentage}
                                    >
                                        Add
                                    </Button>
                                    <Button appearance="secondary" onClick={() => setDrawerOpen(false)}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DrawerBody>
                </Drawer>
            </>
        );
    };

    const renderDisplayField = (field: FieldDefinition): React.ReactNode => {
        if (!_project) return '-';

        // Handle array fields specially
        if (field.isArray) {
            return null; // Handled separately
        }

        // Special case for Funding Source (Foerdergeber) - display text version
        if (field.key === 'projektauftraggeber') {
            const displayValue = ((_project as unknown) as Record<string, unknown>)['foerdergeberNeu'] as string | undefined;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <div className={styles.fieldValue}>{displayValue ? String(displayValue) : <span className={styles.emptyValue}>-</span>}</div>
                </div>
            );
        }

        // Special case for Funding Track (Foerderschiene) - display text version
        if (field.key === 'foerderschiene') {
            const displayValue = ((_project as unknown) as Record<string, unknown>)['foerderschiene'] as string | undefined;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <div className={styles.fieldValue}>{displayValue ? String(displayValue) : <span className={styles.emptyValue}>-</span>}</div>
                </div>
            );
        }

        // Special case for Research Staff Member (Zustaendiger Ma Forschung)
        if (field.key === 'zustaendigerMaForschung') {
            const displayValue = _project.zustaendigerMaForschung;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <div className={styles.fieldValue}>{displayValue ? String(displayValue) : <span className={styles.emptyValue}>-</span>}</div>
                </div>
            );
        }

        // Special case for Lead Partner - display with boolean styling
        if (field.key === 'leadpartner') {
            const displayValue = _project.leadpartner;
            let displayText = '';
            let isTrueValue = false;

            if (displayValue === true) {
                displayText = 'Yes';
                isTrueValue = true;
            } else if (displayValue === false) {
                displayText = 'No';
                isTrueValue = false;
            } else if (displayValue === null) {
                displayText = 'No Consortium';
                isTrueValue = false;
            } else {
                return (
                    <div className={styles.fieldBox}>
                        <div className={styles.fieldLabel}>{field.label}</div>
                        <span className={styles.emptyValue}>-</span>
                    </div>
                );
            }

            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <div className={`${styles.booleanValue} ${isTrueValue ? styles.booleanTrue : styles.booleanFalse}`}>
                        {displayText}
                    </div>
                </div>
            );
        }

        // Special case for Research Focus (ForschungsschwerpunktIntern) - display text version
        if (field.key === 'forschungsschwerpunktInternID') {
            const displayValue = _project.forschungsschwerpunktIntern;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <div className={styles.fieldValue}>{displayValue ? String(displayValue) : <span className={styles.emptyValue}>-</span>}</div>
                </div>
            );
        }

        // Special case for Research Focus (ÖFOS 2012) - display text version
        if (field.key === 'forschungsschwerpunkt2012ID') {
            const displayValue = _project.forschungsschwerpunkt2012;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <div className={styles.fieldValue}>{displayValue ? String(displayValue) : <span className={styles.emptyValue}>-</span>}</div>
                </div>
            );
        }

        // Handle boolean fields
        if (field.isBoolean) {
            let displayValue: string | number | boolean | undefined;

            // For auftragsforschung, try to use the AsText version
            if (field.key === 'auftragsforschung') {
                displayValue = ((_project as unknown) as Record<string, unknown>)['auftragsforschungAsText'] as string | number | boolean | undefined;
            } else {
                const key = field.key as keyof ProjektItem;
                displayValue = _project[key] as string | number | boolean | undefined;
            }

            const isTrue = displayValue === true || displayValue === 'Ja' || displayValue === 'Yes' || displayValue === 'true' || displayValue === 1;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <div className={`${styles.booleanValue} ${isTrue ? styles.booleanTrue : styles.booleanFalse}`}>
                        {isTrue ? 'Yes' : 'No'}
                    </div>
                </div>
            );
        }

        // Handle regular string fields
        const key = field.key as keyof ProjektItem;
        const value = _project[key];
        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{field.label}</div>
                <div className={styles.fieldValue}>{value ? String(value) : <span className={styles.emptyValue}>-</span>}</div>
            </div>
        );
    };

    const renderEditField = (field: FieldDefinition): React.ReactNode => {
        const value = String(editedValues[field.key] || '');

        // Special case for Funding Source (Foerdergeber)
        if (field.key === 'projektauftraggeber') {
            const selectedValue = editedValues.projektauftraggeber as string;
            const displayText = foerdergeberNeuOptions.find(
                (opt) => opt.value === selectedValue
            )?.text || '';

            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <Combobox
                        value={displayText}
                        onOptionSelect={(_, option) => {
                            const selectedOption = foerdergeberNeuOptions.find(
                                (opt) => opt.value === option.optionValue
                            );
                            if (selectedOption) {
                                handleFieldChange('projektauftraggeber', selectedOption.value);
                            }
                        }}
                        placeholder="Select funding source"
                        style={{ width: '100%' }}
                    >
                        {foerdergeberNeuOptions.map((option, index) => (
                            <Option key={`${option.value}-${index}`} value={option.value}>
                                {option.text}
                            </Option>
                        ))}
                    </Combobox>
                </div>
            );
        }

        // Special case for Source of Funds (Mittelherkunft) - disabled in edit mode
        if (field.key === 'mittelherkunft') {
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <Input
                        value={value}
                        disabled
                        title="This field will be calculated by the API"
                        className={styles.inputField}
                    />
                </div>
            );
        }

        // Special case for Funding Track (Foerderschiene)
        if (field.key === 'foerderschiene') {
            const selectedValue = editedValues.foerderschiene as string;
            const displayText = foerderschieneOptions.find(
                (opt) => opt.value === selectedValue
            )?.text || '';

            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <Combobox
                        value={displayText}
                        onOptionSelect={(_, option) => {
                            const selectedOption = foerderschieneOptions.find(
                                (opt) => opt.value === option.optionValue
                            );
                            if (selectedOption) {
                                handleFieldChange('foerderschiene', selectedOption.value);
                            }
                        }}
                        placeholder="Select funding track (optional)"
                        style={{ width: '100%' }}
                    >
                        {foerderschieneOptions.map((option, index) => (
                            <Option key={`${option.value}-${index}`} value={option.value}>
                                {option.text}
                            </Option>
                        ))}
                    </Combobox>
                </div>
            );
        }

        // Special case for Research Staff Member (Zustaendiger Ma Forschung) - Combobox with search
        if (field.key === 'zustaendigerMaForschung') {
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <Combobox
                        placeholder="Enter at least 2 characters..."
                        value={forschungMemberSearchInputValue}
                        onOptionSelect={(_, option) => {
                            const memberId = option.optionValue || '';
                            const member = mitarbeiterOptions.find((m) => m.personalID === parseInt(memberId));
                            if (member) {
                                const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                setForschungMemberSearchInputValue(fullName);
                                handleFieldChange(field.key, memberId);
                            }
                        }}
                        onChange={(ev) => {
                            const searchValue = (ev.target as HTMLInputElement).value;
                            setForschungMemberSearchInputValue(searchValue);
                            if (searchValue === '') {
                                handleFieldChange(field.key, '');
                            }
                            handleForschungMemberSearch(searchValue);
                        }}
                        appearance="outline"
                    >
                        {forschungMemberSearchResults.map((member) => {
                            const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                            return (
                                <Option
                                    key={member.personalID}
                                    value={member.personalID.toString()}
                                    text={fullName}
                                >
                                    {fullName}
                                </Option>
                            );
                        })}
                    </Combobox>
                </div>
            );
        }

        // Special case for Lead Partner (RadioGroup)
        if (field.key === 'leadpartner') {
            const currentValue = editedValues.leadpartner as boolean | null;
            let selectedValue: string;
            if (currentValue === true) {
                selectedValue = 'yes';
            } else if (currentValue === false) {
                selectedValue = 'no';
            } else {
                selectedValue = 'no-consortium';
            }

            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <RadioGroup
                        value={selectedValue}
                        layout='horizontal'
                        onChange={(_: any, data: any) => {
                            if (data.value === 'yes') {
                                handleFieldChange('leadpartner', true);
                            } else if (data.value === 'no') {
                                handleFieldChange('leadpartner', false);
                            } else {
                                handleFieldChange('leadpartner', null);
                            }
                        }}
                    >
                        <Radio value="yes" label="Yes" />
                        <Radio value="no" label="No" />
                        <Radio value="no-consortium" label="No Consortium" />
                    </RadioGroup>
                </div>
            );
        }

        // Special case for ForschungsschwerpunktIntern single select
        if (field.key === 'forschungsschwerpunktInternID') {
            const isDisabled = _project?.projektartID !== 1 && _project?.projektartID !== 10;
            const selectedValue = String(editedValues.forschungsschwerpunktInternID || '');
            const selectedOption = forschungsschwerpunkteOptions.find((opt) => opt.value === parseInt(selectedValue, 10));
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <Combobox
                        value={selectedOption?.text || ''}
                        selectedOptions={selectedValue ? [selectedValue] : []}
                        onOptionSelect={(_ev, data) => {
                            if (isDisabled) return;
                            setEditedValues((prev) => ({
                                ...prev,
                                forschungsschwerpunktInternID: data.optionValue || '',
                            }));
                        }}
                        style={{ width: '100%' }}
                        placeholder="Select research focus area"
                        disabled={isDisabled}
                    >
                        {forschungsschwerpunkteOptions.map((option, index) => {
                            return (
                                <Option key={`${option.value}-${index}`} value={String(option.value)}>
                                    {option.text}
                                </Option>
                            );
                        })}
                    </Combobox>
                </div>
            );
        }

        // Special case for Research Focus (ÖFOS 2012) - typeable combobox that filters by the typed text
        if (field.key === 'forschungsschwerpunkt2012ID') {
            const isDisabled = _project?.projektartID !== 1 && _project?.projektartID !== 10;
            const selectedValue = String(editedValues.forschungsschwerpunkt2012ID || '');
            const selectedOption = oefosOptions.find((opt) => opt.value === parseInt(selectedValue, 10));
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <Combobox
                        value={oefosQuery ?? selectedOption?.text ?? ''}
                        selectedOptions={selectedValue ? [selectedValue] : []}
                        onChange={(ev) => {
                            const text = (ev.target as HTMLInputElement).value;
                            setOefosQuery(text);
                            if (text === '') {
                                setEditedValues((prev) => ({
                                    ...prev,
                                    forschungsschwerpunkt2012ID: '',
                                }));
                            }
                        }}
                        onOptionSelect={(_ev, data) => {
                            setOefosQuery(null);
                            setEditedValues((prev) => ({
                                ...prev,
                                forschungsschwerpunkt2012ID: data.optionValue || '',
                            }));
                        }}
                        onOpenChange={(_ev, data) => {
                            if (!data.open) setOefosQuery(null);
                        }}
                        style={{ width: '100%' }}
                        placeholder="Type to search research focus area"
                        disabled={isDisabled}
                    >
                        {filteredOefosOptions.length > 0 ? filteredOefosOptions.map((option) => (
                            <Option key={option.value} value={String(option.value)} text={option.text}>
                                {option.text}
                            </Option>
                        )) : (
                            <Option value="" disabled text="">
                                No matches
                            </Option>
                        )}
                    </Combobox>
                </div>
            );
        }

        // Special case for Nachhaltigkeitsziele multiselect
        if (field.key === 'nachhaltigkeitszieleCombinedAsText') {
            const selectedValues = (editedValues.selectedNachhaltigkeitsziele || []) as number[];
            const selectedTexts = selectedValues
                .map((id) => nachhaltigkeitszielOptions.find((opt) => opt.value === id)?.text)
                .filter((text) => text !== undefined);
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <Combobox
                        multiselect
                        value={selectedTexts.join(', ')}
                        selectedOptions={selectedValues.map((v) => String(v))}
                        onOptionSelect={(_ev, data) => {
                            const value = parseInt(data.optionValue || '0', 10);
                            const updated = selectedValues.includes(value)
                                ? selectedValues.filter((v) => v !== value)
                                : [...selectedValues, value];
                            setEditedValues((prev) => ({
                                ...prev,
                                selectedNachhaltigkeitsziele: updated,
                            }));
                        }}
                        style={{ width: '100%' }}
                        placeholder="Select sustainability goals"
                    >
                        {nachhaltigkeitszielOptions.map((option, index) => {
                            return (
                                <Option key={`${option.value}-${index}`} value={String(option.value)}>
                                    {option.text}
                                </Option>
                            );
                        })}
                    </Combobox>
                </div>
            );
        }

        if (field.isBoolean) {
            const isTrue = value === 'true' || value === 'true' || value === '1' || editedValues[field.key] === true;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <div style={{ marginTop: '2px' }}>
                        <Checkbox
                            checked={isTrue}

                            onChange={(_ev: any, data: any) => {
                                handleFieldChange(field.key as string, data.checked === true);
                            }}
                        />
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{field.label}</div>
                <Input
                    value={value}
                    onChange={(_ev, data) => {
                        handleFieldChange(String(field.key), data.value);
                    }}
                    className={styles.inputField}
                />
            </div>
        );
    };

    const renderField = (field: FieldDefinition) => {
        // Look up permissions from allFieldPermissions array
        const fieldPermConfig = allFieldPermissions.find((f) => f.key === field.key);
        const fieldPermissions = fieldPermConfig?.permissions;

        // Check if field is editable for this user
        const isFieldEditable = fieldPermissions ?
            canEditField(fieldPermissions, selectedRole, _project, configUserInfo) :
            false;

        // Check if field is visible for this user (read permission)
        const isFieldVisible = fieldPermissions ?
            canViewField(fieldPermissions, selectedRole, _project, configUserInfo) :
            true; // Default to visible if no permissions specified

        // If field is not visible, don't render it
        if (!isFieldVisible) {
            return null;
        }

        // Render in edit mode if user can edit and is editing; otherwise display mode.
        // Required-but-unset fields are framed in both modes.
        const content = isFieldEditable && isEditing && field.editable !== false
            ? renderEditField(field)
            : renderDisplayField(field);

        return (
            <RequiredFieldFrame isMissing={isRequiredFieldMissing(missingFields, field.key)}>
                {content}
            </RequiredFieldFrame>
        );
    };

    return (
        <div className={styles.container}>
            {isSaving && (
                <MessageBar>
                    <MessageBarBody>
                        <MessageBarTitle>Saving...</MessageBarTitle>
                        Please wait while your changes are being saved.
                    </MessageBarBody>
                </MessageBar>
            )}
            {showSuccessMessage && (
                <MessageBar intent="success">
                    <MessageBarBody>
                        <MessageBarTitle>Success</MessageBarTitle>
                        ESG & Funding information saved successfully!
                    </MessageBarBody>
                </MessageBar>
            )}
            {errorMessage && (
                <MessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>Error</MessageBarTitle>
                        {errorMessage}
                    </MessageBarBody>
                </MessageBar>
            )}
            {/* Header with Edit Button */}
            {canEditAny && (
                <div className={styles.header}>
                    <div className={styles.headerTitle}>ESG & Funding</div>
                    <div>
                        {isEditing ? (
                            <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
                                <Button
                                    appearance="primary"
                                    icon={<Save24Regular />}
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    appearance="secondary"
                                    icon={<Dismiss24Regular />}
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                            </div>
                        ) : (
                            <Button
                                appearance="outline"
                                icon={<Edit24Regular />}
                                onClick={() => setIsEditing(true)}
                            >
                                Edit
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {dataGroups.map((group) => (
                <div key={group.title} className={styles.categorySection}>
                    <div className={styles.categoryHeader}>{group.title}</div>
                    <div className={styles.fieldsContainer}>
                        {group.fields.map((field) => {
                            // Handle array fields
                            if (field.isArray && field.key === 'studiengangZuordnung') {
                                return (
                                    <div key={field.label}>
                                        <RequiredFieldFrame isMissing={isRequiredFieldMissing(missingFields, field.key)}>
                                            {renderStudiengangTable()}
                                        </RequiredFieldFrame>
                                    </div>
                                );
                            }

                            return (
                                <div key={field.label}>
                                    {renderField(field)}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* Footer Save/Cancel Buttons */}
            {isEditing && canEditAny && (
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, marginTop: tokens.spacingVerticalL, borderTop: `1px solid ${tokens.colorNeutralStroke2}`, paddingTop: tokens.spacingVerticalL }}>
                    <Button
                        appearance="primary"
                        icon={<Save24Regular />}
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        Save
                    </Button>
                    <Button
                        appearance="secondary"
                        icon={<Dismiss24Regular />}
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                </div>
            )}
        </div>
    );
};

export default EckdatenESGFunding;
